import { NavLink, useLocation } from "react-router-dom";
import { useMemo, type ReactNode } from "react";
import { NAV, PHASES, PHASE_LABEL, navByRoute, type Phase } from "../../nav";
import { BeispieldatenTag } from "./ui";
import { useApp } from "../../store/AppStore";
import { formatNum } from "../../domain/rules";

// Bewusst fester Extraktzeitpunkt der Beispieldaten (kein Live-System).
import { traceWindowLabel } from "../../data/traceMeta";
const EXTRACT_DATE = "25.09.2026";

function DataStamp() {
  const { ds, dataSource } = useApp();
  const stamp = useMemo(() => {
    const systems = new Set<string>();
    const clients = new Set<string>();
    for (const a of ds.accounts) {
      if (a.sid) systems.add(a.sid);
      if (a.client) clients.add(`${a.sid}/${a.client}`);
    }
    return { systems: systems.size, clients: clients.size };
  }, [ds]);
  return (
    <span>
      Quelle: {dataSource === "seed" ? `Seed-Export ${formatNum(ds.persons.length)} Personen / ${formatNum(ds.accounts.length)} Konten` : "Demo-Generator"} ·{" "}
      Datenextrakt {EXTRACT_DATE} · Trace {traceWindowLabel()} · {formatNum(stamp.systems)} Systeme mit Konten · {formatNum(stamp.clients)} von{" "}
      {formatNum(stamp.clients)} Mandanten erfasst
    </span>
  );
}

function PhaseBar() {
  const { pathname } = useLocation();
  const cur = navByRoute(pathname);
  const curPhase = cur && cur.phase !== "übergreifend" ? (cur.phase as Phase) : null;
  const curIdx = curPhase ? PHASES.indexOf(curPhase) : -1;
  const isOverview = cur?.phase === "übergreifend";
  return (
    <div className="bg-card border-b border-soft">
      <div className="mx-auto flex max-w-[1400px] items-center gap-1 overflow-x-auto px-4 py-2 sm:px-6">
        {isOverview && (
          <span className="mr-1 rounded-md px-2.5 py-1 text-[13px] font-semibold" style={{ backgroundColor: "var(--color-brand-600)", color: "#fff" }}>Überblick</span>
        )}
        {PHASES.map((p, i) => {
          const active = i === curIdx;
          return (
            <div key={p} className="flex items-center">
              <div className="flex items-center gap-2 rounded-md px-2.5 py-1"
                style={{ backgroundColor: active ? "var(--color-brand-600)" : "transparent" }}>
                <span className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold"
                  style={{ backgroundColor: active ? "rgba(255,255,255,0.25)" : "var(--surface-sunken)", color: active ? "#fff" : "var(--text-faint)" }}>
                  {i + 1}
                </span>
                <span className="text-[13px] font-semibold whitespace-nowrap"
                  style={{ color: active ? "#fff" : "var(--text-faint)" }}>{PHASE_LABEL[p]}</span>
              </div>
              {i < PHASES.length - 1 && <span className="text-faint px-0.5">→</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <div className="bg-surface min-h-screen">
      <header className="bg-card sticky top-0 z-30 border-b border-soft">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg font-bold text-white" style={{ backgroundColor: "var(--color-brand-600)" }}>R</div>
            <div>
              <div className="text-strong text-[15px] leading-tight font-bold">RISE License Cockpit</div>
              <div className="text-faint text-xs leading-tight">Arvato SE · Werkzeug für RISE-Lizenzmanagement</div>
            </div>
          </div>
          <BeispieldatenTag />
        </div>
      </header>

      <PhaseBar />

      <div className="mx-auto flex max-w-[1400px] gap-6 px-4 py-6 sm:px-6">
        <aside className="hidden w-56 shrink-0 lg:block">
          <nav className="sticky top-[76px] space-y-1">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.route} to={item.route} end={item.route === "/"}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm"
                  style={({ isActive }) => isActive
                    ? { backgroundColor: "var(--color-brand-600)", color: "#fff", fontWeight: 600 }
                    : { color: "var(--text-dim)" }}>
                  <Icon size={17} className="shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {!item.ready && <span className="text-faint ml-auto text-[10px]">bald</span>}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-soft bg-card lg:hidden">
          <div className="flex gap-1 overflow-x-auto px-2 py-2">
            {NAV.map((item) => {
              const Icon = item.icon; const active = pathname === item.route;
              return (
                <NavLink key={item.route} to={item.route} end={item.route === "/"}
                  className="flex shrink-0 flex-col items-center gap-0.5 rounded-lg px-2.5 py-1"
                  style={{ color: active ? "var(--color-brand-500)" : "var(--text-faint)" }}>
                  <Icon size={18} /><span className="text-[10px]">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>

        <main className="min-w-0 flex-1 pb-24 lg:pb-0">{children}</main>
      </div>

      <footer className="border-t border-soft">
        <div className="text-faint mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-2 px-4 py-4 text-xs sm:px-6">
          <DataStamp />
          <span>Beispieldaten · kein Produktivsystem</span>
          <span>mindsquare AG</span>
        </div>
      </footer>
    </div>
  );
}

export function PageHeader({ route, right }: { route: string; right?: ReactNode }) {
  const nav = navByRoute(route);
  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-strong text-2xl font-bold tracking-tight">{nav?.label}</h1>
        {right}
      </div>
      <p className="text-dim mt-1.5 text-[15px]">
        <span className="text-faint">Diese Seite beantwortet:</span>{" "}
        <span className="text-normal font-medium">{nav?.question}</span>
      </p>
    </div>
  );
}
